import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

const SCOPE_API = '/v1/scope';

export interface ScopeData {
  id?: string;
  heading: string;
  text: Record<string, string>;
  updatedBy?: string;
  updatedAt?: string;
  history?: ScopeHistory[];
}

export interface ScopeHistory {
  timestamp: any;
  description: string;
  updatedBy: string;
}

/**
 * Fetches all scope items via Thalassa API
 * @param tenantId - The tenant ID
 * @returns Promise with the scope data
 */
export async function getAllScopeItems(tenantId: string): Promise<ScopeData> {
  console.log('[scopeService] getAllScopeItems called with:', tenantId);
  
  try {
    const res = await deiterateAPI.GET(SCOPE_API, {
      mode: 'cors',
      backend: 'thalassa',
      headers: { 'X-Tenancy-Id': tenantId, 'Company-ID': tenantId }
    });

    return res.data || { heading: 'MSG Scope', text: {} };
  } catch (error) {
    console.error('[scopeService] Error getting scope data:', error);
    throw error;
  }
}

/**
 * Creates a new scope item via Thalassa API
 * @param tenantId - The tenant ID
 * @param data - The scope data to create
 * @returns Promise that resolves when the scope data is created
 */
export async function createScopeItem(tenantId: string, data: ScopeData): Promise<void> {
  console.log('[scopeService] createScopeItem called with:', { tenantId, data });
  
  try {
    await deiterateAPI.POST(SCOPE_API, {
      mode: 'cors',
      backend: 'thalassa',
      headers: { 'X-Tenancy-Id': tenantId, 'Company-ID': tenantId },
      body: JSON.stringify(data)
    });
  } catch (error) {
    console.error('[scopeService] Error saving scope data:', error);
    throw error;
  }
}

/**
 * Updates an existing scope item via Thalassa API
 * @param tenantId - The tenant ID
 * @param id - The ID of the scope item to update
 * @param updates - The updated scope data
 * @returns Promise that resolves when the scope data is updated
 */
export async function updateScopeItem(tenantId: string, id: string, updates: ScopeData): Promise<void> {
  console.log('[scopeService] updateScopeItem called with:', { tenantId, id, updates });
  
  try {
    await deiterateAPI.PUT(`${SCOPE_API}/${id}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: { 'X-Tenancy-Id': tenantId, 'Company-ID': tenantId },
      body: JSON.stringify(updates)
    });
  } catch (error) {
    console.error('[scopeService] Error updating scope item:', error);
    throw error;
  }
}

/**
 * Deletes a scope item via Thalassa API
 * @param tenantId - The tenant ID
 * @param scopeID - The ID of the scope item to delete
 * @returns Promise that resolves when the scope item is deleted
 */
export async function deleteScopeItem(tenantId: string, scopeID: string): Promise<void> {
  console.log('[scopeService] deleteScopeItem called with:', { tenantId, scopeID });
  
  try {
    await deiterateAPI.DELETE(`${SCOPE_API}/${scopeID}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: { 'X-Tenancy-Id': tenantId, 'Company-ID': tenantId }
    });
    console.log('[scopeService] Deleted scope item:', scopeID);
  } catch (error) {
    console.error('[scopeService] Error deleting scope item:', error);
    throw error;
  }
}

export default {
  getAllScopeItems,
  createScopeItem,
  updateScopeItem,
  deleteScopeItem
};
